import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { CoachesDataService, CoachesColumnsService } from '../../services';
import { DialogCoachFormComponent } from './coach-form/coach-form.component';
import { DialogCoachDetailsComponent } from './coach-details/coach-details.component';
import { Coach, CoachesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-coaches',
  templateUrl: './coaches.component.html',
  styleUrls: ['./coaches.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class CoachesComponent implements OnInit, OnDestroy {

  private coachAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private coachEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private coachDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public coachToDelete: Coach = {};
  public coaches: Coach[] = [];
  public coach: Coach = {};
  public selectedCoaches: Coach[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private coachesDataService: CoachesDataService,
    private coachesColumnsService: CoachesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('CoachesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Coaches - Management');
    this.primengConfig.ripple = true;
    this.getCoaches();
  }

  ngOnDestroy(): void {
    console.log('CoachesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getCoaches(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.coachesDataService.getCoaches(this.parameters).subscribe((response: CoachesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.coaches = response.results;
        } else {
          this.coaches = this.coaches.concat(response.results);
        }
        if (response.inlineCount > this.coaches.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getCoaches();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COACHESNOTLOADED')});
    });
  }

  importCoaches(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.coachesDataService.addCoaches(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('COACHESCREATED')});
                if (proceed === j) {
                  this.getCoaches();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COACHESNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COACHESNOTCREATED') + ' ' + msg});
            });
          } else {
            this.coachesDataService.updateCoaches(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('COACHESUPDATED')});
                if (proceed === j) {
                  this.getCoaches();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COACHESNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('COACHESNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.coachesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('coaches.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedCoaches() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Coaches?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.coachesDataService.deleteCoaches(this.selectedCoaches).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Coaches Deleted', life: 3000});
            this.getCoaches();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Coaches not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Coaches not deleted'});
        });
        this.selectedCoaches = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.coaches, this.columns, 'Coaches');
  }

  exportExcel() {
    ToolsService.exportExcel(this.coaches, this.columns, 'Coaches');
  }

  exportCsv() {
    ToolsService.exportCsv(this.coaches, this.columns, 'Coaches');
  }

  refresh() {
    this.coachesDataService.clearCache();
    this.getCoaches();
  }

  coachAdd(): void {
    if (!this.navigation.getParamValue('coaches.addInDialog', true)) {
      this.router.navigate(['/coaches/add']);
      return;
    }
    this.coachAddDialog = this.dialogService.open(DialogCoachFormComponent, {
      header: '', // this.translate.instant('NEWCOACH'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        coach: {id: null},
        users: this.coachesColumnsService.users,
      },
    });

    this.coachAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Coach added'});
        this.getCoaches();
      }
    });
  }

  coachEdit(coach: Coach): void {
    if (!this.navigation.getParamValue('coaches.editInDialog', true)) {
      this.router.navigate(['/coaches/edit/' + coach.id]);
      return;
    }
    this.coachEditDialog = this.dialogService.open(DialogCoachFormComponent, {
      header: '', // this.translate.instant('EDITCOACH') + ' ' + coach.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        coach: coach,
        users: this.coachesColumnsService.users,
      },
    });

    this.coachEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Coach updated'});
        this.getCoaches();
      }
    });
  }

  coachDetails(coach: Coach): void {
    if (!this.navigation.getParamValue('coaches.detailsInDialog', true)) {
      this.router.navigate(['/coaches/details/' + coach.id]);
      return;
    }
    this.coachDetailsDialog = this.dialogService.open(DialogCoachDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        coach: coach,
      }
    });
  }

  coachDelete(coach: Coach): void {
    this.coachToDelete = coach;
    this.messageService.clear();
    this.messageService.add({
      key: 'coachDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + coach.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.coachesDataService.deleteCoach(this.coachToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Coach deleted'});
      this.getCoaches();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Coach not deleted'});
    });
  }

  onDeleteReject(): void {
    this.coachToDelete = {};
    this.messageService.clear('coachDelete');
  }

}
